import { Download, ShieldCheck, Info } from "lucide-react";

/**
 * mode:
 *   - "saved": audio has been persisted (Trial/Solo user or Read-Only history view)
 *   - "demo":  in-browser only (public Demo — nothing is uploaded)
 *   - undefined: no reassurance line
 */
export default function AudioPlayer({ src, label = "Recorded audio", mode }) {
  if (!src) return null;
  const isSaved = mode === "saved";
  const isDemo = mode === "demo";
  return (
    <div
      data-testid="audio-player"
      className="mt-3 bg-[#F0FDF4] border border-[#D1FAE5] rounded-xl overflow-hidden"
    >
      <div className="flex items-center gap-3 px-3 py-2">
        <div className="font-figtree text-[11px] uppercase tracking-[0.18em] font-semibold text-[#065F46] flex-shrink-0">
          {label}
        </div>
        <audio
          controls
          src={src}
          className="h-9 flex-1 min-w-0"
          data-testid="audio-player-element"
        />
        <a
          href={src}
          download={`therapynote-recording-${Date.now()}.webm`}
          data-testid="audio-player-download"
          className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-figtree font-medium text-[#065F46] hover:bg-[#D1FAE5] transition-colors"
        >
          <Download className="w-3 h-3" />
          Save
        </a>
      </div>

      {(isSaved || isDemo) && (
        <div
          data-testid={isSaved ? "audio-safety-saved" : "audio-safety-demo"}
          className={
            isSaved
              ? "flex items-center gap-2 px-3 py-1.5 border-t border-[#D1FAE5] bg-[#ECFDF5]"
              : "flex items-center gap-2 px-3 py-1.5 border-t border-[#FDE68A] bg-[#FEF3C7]"
          }
        >
          <SafetyPulse tone={isSaved ? "saved" : "demo"} />
          {isSaved ? (
            <>
              <ShieldCheck className="w-3.5 h-3.5 text-[#059669] flex-shrink-0" />
              <span className="font-figtree text-[11px] sm:text-xs font-medium text-[#065F46]">
                Encrypted &amp; saved to this patient&apos;s history.
              </span>
            </>
          ) : (
            <>
              <Info className="w-3.5 h-3.5 text-[#B45309] flex-shrink-0" />
              <span className="font-figtree text-[11px] sm:text-xs font-medium text-[#78350F]">
                Demo recording — playable in this browser only.{" "}
                <a
                  href="/signup"
                  className="underline hover:text-[#065F46] font-semibold"
                  data-testid="audio-safety-demo-signup"
                >
                  Start free trial to save it.
                </a>
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Tiny 3-bar pulse — pure CSS to avoid a heavy waveform lib. Reinforces
 * "your audio is being cared for" without adding runtime cost.
 */
function SafetyPulse({ tone }) {
  const color = tone === "saved" ? "#10B981" : "#D97706";
  return (
    <div
      aria-hidden="true"
      className="flex items-end gap-[2px] h-3 flex-shrink-0"
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-[3px] rounded-sm animate-tn-pulse"
          style={{
            backgroundColor: color,
            animationDelay: `${i * 140}ms`,
          }}
        />
      ))}
    </div>
  );
}
